import { useEffect, useState } from "react";
import { KeyRound, Pencil, Trash2, UserPlus, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const inputCls =
  "h-11 w-full rounded-xl border border-slate-200 bg-paper px-4 text-sm font-medium text-ink outline-none transition-colors duration-200 focus:border-royal";

const PERMISSIONS = [
  ["bookings", "Bookings & Deliveries"],
  ["offers", "Offers & Discounts"],
  ["customers", "Customers & Data"],
  ["inventory", "Inventory & Tote pool"],
];

const permLabel = (p) => PERMISSIONS.find(([k]) => k === p)?.[1] || p;

const PermChecks = ({ value, onChange, prefix }) => (
  <div className="grid gap-2 sm:grid-cols-2">
    {PERMISSIONS.map(([key, label]) => (
      <label
        key={key}
        className={`flex cursor-pointer items-center gap-2.5 rounded-xl border px-3.5 py-2.5 text-xs font-semibold transition-colors duration-200 ${
          value.includes(key) ? "border-royal bg-royal-light text-royal" : "border-slate-200 text-slate-500 hover:border-slate-300"
        }`}
      >
        <input
          type="checkbox"
          checked={value.includes(key)}
          onChange={() => onChange(value.includes(key) ? value.filter((p) => p !== key) : [...value, key])}
          data-testid={`${prefix}-perm-${key}`}
          className="h-4 w-4 accent-[#1D4ED8]"
        />
        {label}
      </label>
    ))}
  </div>
);

export const TeamTab = () => {
  const { user } = useAuth();
  const [team, setTeam] = useState([]);
  const [email, setEmail] = useState("");
  const [perms, setPerms] = useState(PERMISSIONS.map(([k]) => k));
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null); // email being edited
  const [editPerms, setEditPerms] = useState([]);
  const [newPassword, setNewPassword] = useState("");
  const [pwBusy, setPwBusy] = useState(false);
  const isOwner = Boolean(user?.is_owner);

  const load = () => api.get("/admin/team").then((r) => setTeam(r.data)).catch(() => {});
  useEffect(() => { if (isOwner) load(); }, [isOwner]);

  if (!isOwner) {
    return (
      <p className="mt-8 rounded-2xl border border-slate-200 bg-white p-6 text-xs font-medium text-slate-500" data-testid="team-owner-locked">
        Only David or Charlotte can manage company access.
      </p>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/team", { email, permissions: perms });
      toast.success(`${email} can now sign in with the company password`);
      setEmail("");
      setPerms(PERMISSIONS.map(([k]) => k));
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const startEdit = (m) => {
    setEditing(m.email);
    setEditPerms(m.permissions);
  };

  const saveEdit = async (m) => {
    try {
      await api.patch(`/admin/team/${m.email}`, { permissions: editPerms });
      toast.success(`Permissions updated for ${m.email}`);
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const removeMember = async (m) => {
    if (!window.confirm(`Remove company access for ${m.email}? Their sign-in stops working immediately.`)) return;
    try {
      await api.delete(`/admin/team/${m.email}`);
      toast.success(`${m.email} removed from company access`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    setPwBusy(true);
    try {
      await api.post("/admin/company-password", { new_password: newPassword });
      toast.success("Company password updated — share it with your team");
      setNewPassword("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setPwBusy(false);
    }
  };

  return (
    <div data-testid="team-tab" className="mt-8 grid gap-8 lg:grid-cols-2">
      <div>
        <h3 className="font-display text-lg font-bold text-ink">Company access</h3>
        <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.15em] text-slate-400">
          everyone here signs in with their email + the company password
        </p>
        <div className="mt-4 space-y-3" data-testid="team-list">
          {team.map((m) => (
            <div key={m.id} className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={`team-entry-${m.email}`}>
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate font-semibold text-ink">{m.email}</p>
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {m.owner ? (
                      <span className="rounded-full bg-gold/20 px-2.5 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-amber-700">
                        Owner — full access
                      </span>
                    ) : m.permissions.length ? (
                      m.permissions.map((p) => (
                        <span key={p} className="rounded-full bg-royal-light px-2.5 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-royal">
                          {permLabel(p)}
                        </span>
                      ))
                    ) : (
                      <span className="rounded-full bg-slate-100 px-2.5 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400">
                        Sign-in only
                      </span>
                    )}
                  </div>
                </div>
                {!m.owner && (
                  <div className="flex shrink-0 items-center gap-2">
                    <button
                      onClick={() => (editing === m.email ? setEditing(null) : startEdit(m))}
                      data-testid={`team-edit-${m.email}`}
                      aria-label={`Edit permissions for ${m.email}`}
                      className="grid h-9 w-9 place-items-center rounded-full border border-slate-200 text-slate-400 transition-colors duration-200 hover:border-royal hover:text-royal"
                    >
                      {editing === m.email ? <X className="h-4 w-4" /> : <Pencil className="h-4 w-4" />}
                    </button>
                    <button
                      onClick={() => removeMember(m)}
                      data-testid={`team-remove-${m.email}`}
                      aria-label={`Remove ${m.email}`}
                      className="grid h-9 w-9 place-items-center rounded-full border border-slate-200 text-slate-400 transition-colors duration-200 hover:border-red-300 hover:text-red-600"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>
              {editing === m.email && (
                <div className="mt-4 border-t border-slate-100 pt-4">
                  <PermChecks value={editPerms} onChange={setEditPerms} prefix={`team-edit-${m.email}`} />
                  <button
                    onClick={() => saveEdit(m)}
                    data-testid={`team-save-${m.email}`}
                    className="mt-3 w-full rounded-xl bg-ink py-2.5 text-xs font-semibold text-white transition-colors duration-200 hover:bg-royal"
                  >
                    Save permissions
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      <div>
        <h3 className="font-display text-lg font-bold text-ink">Authorize an employee</h3>
        <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.15em] text-slate-400">
          no personal details needed — just their work email
        </p>
        <form onSubmit={submit} className="mt-4 space-y-4 rounded-2xl border border-slate-200 bg-white p-6" data-testid="team-form">
          <input value={email} onChange={(e) => setEmail(e.target.value)}
                 type="email" placeholder="Employee work email *" data-testid="team-email-input" className={inputCls} required />
          <PermChecks value={perms} onChange={setPerms} prefix="team-add" />
          <button type="submit" disabled={busy}
                  data-testid="team-add-button"
                  className="flex w-full items-center justify-center gap-2 rounded-xl bg-royal py-3.5 text-sm font-semibold text-white transition-all duration-200 hover:bg-royal-dark disabled:opacity-50">
            <UserPlus className="h-4 w-4" /> {busy ? "Authorizing…" : "Authorize access"}
          </button>
          <p className="text-[11px] leading-relaxed text-slate-400">
            They sign in on the regular Sign in page with this email and the company password. Uncheck every box for sign-in-only access to the Command deck home.
          </p>
        </form>

        <div className="mt-8">
          <h3 className="font-display text-lg font-bold text-ink">Company password</h3>
          <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.15em] text-slate-400">
            the single password every authorized email signs in with
          </p>
          <form onSubmit={changePassword} className="mt-4 space-y-3 rounded-2xl border border-gold/40 bg-white p-6" data-testid="company-password-form">
            <input value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                   type="password" placeholder="New company password (6+ characters) *" minLength={6}
                   autoComplete="off" data-testid="new-company-password-input" className={inputCls} required />
            <button type="submit" disabled={pwBusy}
                    data-testid="change-company-password-button"
                    className="flex w-full items-center justify-center gap-2 rounded-xl bg-ink py-3.5 text-sm font-semibold text-white transition-all duration-200 hover:bg-royal disabled:opacity-50">
              <KeyRound className="h-4 w-4" /> {pwBusy ? "Updating…" : "Change company password"}
            </button>
            <p className="text-[11px] leading-relaxed text-slate-400">
              Takes effect immediately for the next sign-in. Only David and Charlotte can change it.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};
